import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

//MUI
import Grid from '@mui/material/Grid2';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Slide from '@mui/material/Slide';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
// import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
//Lab
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';

//MUI ICONS
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArchiveIcon from '@mui/icons-material/Archive';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DataUsageIcon from '@mui/icons-material/DataUsage';
// import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';

// Our Imports 
import { tblData } from './static';
import { SIPPieChart, SIPLineChart } from './charts';
import { AnimatedText, InputLabel } from '../../../assets/component/initComponent';
import { imageExist, formatINR, amountFormatter, numberToWords }  from '../../../assets/js/init';


const Calculator = ({ reached, viewOnce = false }) => {

	//Get Mbl View BreakPoint
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md')); 

	//State
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [show, setShow] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [activeTopic, setActiveTopic] = useState(0);
	const [activeTab, setActiveTab] = useState('1');
	const [showPopper, setShowPopper] = useState(false);
	const [showRslt, setShowRslt] = useState(false);
	const [selectedYear, setSelectedYear] = useState(0);
	const [chartMode, setChartMode] = useState('pie');
	const [isFavorite, setIsFavorite] = useState(false);
	const [formData, setFormData] = useState({
		sipFrequency: 'monthly',
		sipAmount: 1000,
		investmentYear: 5,
		investmentMonth: '0',
		expectedReturn: 6
	});
	const [rsltData, setRsltData] = useState({
		totalInvestment: 60000,
		gainLoss: 10119,
		maturityValue: 70119,
		tableData: tblData,
		sipFrequency: 'monthly',
		sipAmount: 1000,
		investmentYear: 5,
		investmentMonth: '0',
		expectedReturn: 6
	});

	//Ref
	const ref = useRef(null); 
	const secRef = useRef(null);
	const prevTab = useRef(activeTab);
	const inputRef = useRef(null);

	// --- Effect --- //

	useEffect(() => { //Inital + Reset
		if (reached) {
			setActiveTab('1');
			setShow(false);
			setIsFocused(true);
			setTimeout(() => {
				inputRef.current?.focus();
			}, 100);
		}
	}, [reached]);

	// --- Functions --- //

	//Explore Demo Button
	const handleMouseMove = (e) => {
		const rect = secRef.current.getBoundingClientRect();

		setPosition({
			x: e.clientX - rect.left,
			y: e.clientY - rect.top
		});
	};
	
	//Left Content Reveal Based on Hover
	const handleHover = (proVal) => {
		setActiveTopic(proVal);
	};

	//Tab Change
	const handleTabChange = (newValue) => {
		prevTab.current = activeTab;
		setActiveTab(newValue);
		setChartMode('pie');
		setSelectedYear(0);
		if (ref.current) {
			ref.current.scrollTo({ top: 0, behavior: 'instant' });
		}
	};

	//Input Onchange
	const handleChangeInput = (e) => {
		const { name, value } = e.target;
		
		const updatedValue = ['sipFrequency', 'investmentMonth'].includes(name) ? value : value.replace(/\D/g, '');
		setFormData(prev => ({
			...prev,
			[name]: updatedValue
		}));
		setShowRslt(false);
	};

	//Calculate SIP
	const handleCalculate = () => {
		const sipAmount = Number(formData.sipAmount);
		const investmentYear = Number(formData.investmentYear);
		const investmentMonth = Number(formData.investmentMonth);
		const expectedReturn = Number(formData.expectedReturn);
		const frequency = formData.sipFrequency; // daily, weekly, monthly, quarterly

		// Convert everything into months first
		const totalMonths = investmentYear * 12 + investmentMonth;

		// Determine periods per month based on frequency
		let periodsPerMonth;
		switch (frequency) {
		case 'daily': periodsPerMonth = 30; // assume 30 days in month
			break;
		case 'weekly': periodsPerMonth = 4; // approx 4 weeks per month
			break;
		case 'quarterly': periodsPerMonth = 1 / 3; // 1 	quarterly SIP every 3 months
			break;
		case 'monthly':
		default: periodsPerMonth = 1;
		}

		const totalPeriods = Math.round(totalMonths * periodsPerMonth);
		const monthlyRate = expectedReturn / 12 / 100;
		const periodRate = monthlyRate / periodsPerMonth; // rate per period

		let periodWiseData = [];

		// ---- Period-wise calculation ----
		for (let p = 1; p <= totalPeriods; p++) {
			const invested = sipAmount * p;
			const value = sipAmount * ((Math.pow(1 + periodRate, p) - 1) / periodRate) * (1 + periodRate);
			const gainLoss = value - invested;

			periodWiseData.push({
				period: p,
				investedAmount: Math.round(invested),
				maturityValue: Math.round(value),
				gainLoss: Math.round(gainLoss)
			});
		}

		// ---- Year-wise calculation (still by year) ----
		let tableData = [];
		for (let y = 1; y <= investmentYear; y++) {
			// Number of periods in this year
			const endPeriod = Math.round(y * 12 * periodsPerMonth);
			const invested = sipAmount * endPeriod;
			const value = sipAmount * ((Math.pow(1 + periodRate, endPeriod) - 1) / periodRate) * (1 + periodRate);
			const gainLoss = value - invested;

			// Slice periodWiseData for this year's periods
			const periodsInYear = periodWiseData.slice(
				Math.round((y - 1) * 12 * periodsPerMonth),
				endPeriod
			);

			tableData.push({
				year: y,
				investedAmount: Math.round(invested),
				maturityValue: Math.round(value),
				gainLoss: Math.round(gainLoss),
				periods: periodsInYear
			});
		}

		const finalPeriod = periodWiseData[totalPeriods - 1];

		setRsltData({
			totalInvestment: sipAmount * totalPeriods,
			maturityValue: finalPeriod.maturityValue,
			gainLoss: finalPeriod.gainLoss,
			tableData,
			sipFrequency: frequency,
			sipAmount: sipAmount,
			investmentYear: investmentYear,
			investmentMonth: investmentMonth,
			expectedReturn: expectedReturn
		});

		setShowRslt(true);
		setShowPopper(true);
		setTimeout(() => setShowPopper(false), 4000);

		setTimeout(() => {
			ref.current?.scrollTo({
				top: ref.current.scrollHeight,
				behavior: 'smooth'
			});
		}, 500);
	};

	//Reset Calc
	const handleReset = () => {
		setFormData({
			sipFrequency: 'monthly',
			sipAmount: 1000,
			investmentYear: 5,
			investmentMonth: '0',
			expectedReturn: 6
		});
		setShowRslt(false);
		setActiveTab('1');
		if (ref.current) {
			ref.current.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	//Split table Year Split Show
	const handleSplitTable = (proVal) => {
		setSelectedYear(proVal === selectedYear ? 0 : proVal);
	};

	//Chart Mode Pie Or Graph
	const handlechartMode = (proMode) => {
		setChartMode(proMode);
	};	

	//Change Favorite
	const handleFav = () => {
		setIsFavorite(!isFavorite);
	};

	//Make Input Focus
	const handleFocusInput = () => {
		if (!isMobile && show) {
			setActiveTab('1');
			setTimeout(() => {
				inputRef.current?.focus();
			}, 100);
			setShow(false);
		}
	};

	//Get Input Was Focus or Not
	const handleFocus = (proFor) => {
		if (proFor === 'focus') {
			setIsFocused(true);
		} else {
			setIsFocused(false);
		}
	};

	//Get Mouse Enter Into Calc
	const handleMouseInside = (proFrom) => {
		if (proFrom === 'exit') {
			setIsFocused(false);
		}
	};

	//Make Scrollable Calc
	const handleActiveScroll = () => {
		setIsFocused(true);
	};

	const direction = Number(activeTab) > Number(prevTab.current) ? 'left' : 'right';

	const tabList = [
		{
			title: 'Value',
			value: '1',
			icon: <AssignmentIcon/>
		},
		{
			title: 'Reports',
			value: '2',
			icon: <FactCheckIcon/>
		},
		{
			title: 'Chart',
			value: '3',
			icon: <DataUsageIcon/>
		}
	];

	const dropInfo = [
		{
			label: 'Daily',
			value: 'daily'
		},
		{
			label: 'Weekly',
			value: 'weekly'
		},
		{
			label: 'Monthly',
			value: 'monthly'
		},
		{
			label: 'Quarterly',
			value: 'quarterly'
		}
	];

	const monthInfo = [
		{ label: '0', value: '0' },
		{ label: '1', value: '1' },
		{ label: '2', value: '2' },
		{ label: '3', value: '3' },
		{ label: '4', value: '4' },
		{ label: '5', value: '5' },
		{ label: '6', value: '6' },
		{ label: '7', value: '7' },
		{ label: '8', value: '8' },
		{ label: '9', value: '9' },
		{ label: '10', value: '10' },
		{ label: '11', value: '11' }
	];

	const steps = [
		{
			title: 'Choose Your Calculator',
			desc: 'Browse our library of 70+ specialized calculators and pick one that suits your need. '
		},
		{
			title: 'Enter Your Details',
			desc: 'Fill in the required fields for your chosen calculator to generate results tailored to your inputs.'
		},
		{
			title: 'View Reports & Charts',
			desc: 'Review comprehensive reports and charts that transform complex data into clear insights.'
		},
		{
			title: 'Export & Share',
			desc: 'Download your reports in PDF or Excel formats to track your progress or share directly with your financial advisor.'
		}
	];

	//Animation
	const textVariant = {
		hidden: {
			opacity: 0,
			y: 140
			// scale: 0.5
		},
		visible: {
			opacity: 1,
			y: 0,
			// scale: 1,
			transition: {
				delay: 0.3,
				duration: 2,
				ease: [0.22, 1, 0.36, 1] // <-- smooth premium easing (important)
			}
		}
	};

	return(
		<Container className='containerFull' sx={{ position: 'relative', overflow: { xs: 'hidden', sm: 'hidden', md: 'visible' } }}>
			<Box className='randObjBuffCircle'>
				{imageExist({
					required: '/common/objects',
					url: '/buffyCircle.svg',
					alt: 'screenshot',
					height: '100%',
					width: '100%',
					style: { objectFit: 'contain' }
				})}
			</Box>
			<Box className='randObjCoin2'>
				{imageExist({
					required: '/common/objects',
					url: '/coin.svg',
					alt: 'screenshot',
					height: '100%',
					width: '100%',
					style: { objectFit: 'contain' }
				})}
			</Box>
			<Grid container spacing={12} rowSpacing={2} alignItems='center' justifyContent='center' onClick={handleFocusInput} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} ref={secRef} onMouseMove={handleMouseMove}>
				{!reached && show && !isMobile &&
					<button
						className='cursor-btn'
						style={{
							left: position.x,
							top: position.y,
							whiteSpace: 'nowrap'
						}}
					>
						Click to View Demo
					</button>
				}
				<Grid size={{ lg: 6, md: 10, xs: 12 }} sx={{ color: '#fff' }}>
					<Grid container spacing={4}>
						<Grid size={12}>
							<AnimatedText title='How It Works - Explore Smart Financial Calculations' className='font40 fontBolder whiteCl'/>
						</Grid>
						<Grid size={12} sx={{ minHeight: { md: '450px', xs: 'max-contant' } }}>
							<motion.div
								variants={textVariant}
								initial='hidden'
								whileInView='visible'
								viewport={{ once: viewOnce, margin: '-100px' }}
							>
								<Grid container>
									{steps.map((list, index) => {
										const active = activeTopic === index;
										return(
											<Grid size={12} key={index} onMouseEnter={() => handleHover(index)} className='cursorPointer' sx={{ borderBottom: '1px solid #6A6A6A' }} py={3}>
												<Box sx={ active ? { borderLeft: '3px solid #7000FF' } : { borderLeft: '3px solid #000' }} style={{ transform: isMobile ? 'translateX(0px)' : 'translateX(-20px)' }} pl={2}>
													<Typography color={active ? '#fff' : '#CDCDCD'} className='font25'>{list.title}</Typography>
													<Collapse in={active}>
														<Typography color='#CDCDCD' className='font20'>{list.desc}</Typography>
													</Collapse>
												</Box>
											</Grid>
										);
									})}
								</Grid>
							</motion.div>
						</Grid>
					</Grid>
				</Grid>
				{isMobile &&
					<Grid size={12} pt={3}>
						<Typography className='font25 whiteCl fontBold'>Here! Explore Our Demo!</Typography>
					</Grid>
				}
				<Grid size={{ lg: 6, md: 12, xs: 12 }} className='displayCenter' sx={{ position: 'relative' }}>
					<Box className='calcGlassBgA displayCenter'>
						{isFocused && Array.from({ length: 1 }).map((_, index) => {
							const angle = (360 / 9) * index;
							return (
								<Box key={index} className="calcOrbitItem"
									sx={{ transform: `translate(-50%, -50%) rotate(95deg) translate(285px) rotate(-${angle}deg)`, backdropFilter: 'blur(2px)', display: { md: 'block', xs: 'none' } }}
								>
								</Box>
							);
						})}
						<Box className={`calcGlassBgB ${isFocused ? 'calcGlassBgBActive' : 'calcGlassBgBNonActive'}`}></Box>
					</Box>
					<div className='phoneScene' onMouseEnter={() => setShow(false)} onMouseLeave={() => setShow(true)}>
						<motion.div
							className='phoneWrapper'
							initial={{ x: isMobile ? 0 : 250 }} // start outside right
							whileInView={{ x: 0 }}   // move into position
							viewport={{ once: viewOnce, margin: '-200px' }}
							transition={{
								duration: 1.2,
								ease: [0.22, 1, 0.36, 1] // smooth premium easing
							}}
						>
							<Box sx={{ border: '1px solid #fff', padding: '15px', zIndex: 10, borderRadius: '20px' }}  onMouseEnter={handleMouseInside} onMouseLeave={() => handleMouseInside('exit')} onClick={handleActiveScroll}>
								<Box sx={{ bgcolor: '#fff', width: { md: '320px', sm: '320px', xs: '100%' }, borderRadius: '20px' }} {...(isFocused && { 'data-lenis-prevent': true })}>
									<Box sx={{ border: '1px solid #fff', borderRadius: '20px', background: 'linear-gradient(to bottom left, rgba(207,181,255,0.5) 0%, rgba(207,181,255,0.02) 25%)' }}>
										<Grid container spacing={3} p={2} pb={1} sx={{ borderBottom: '1px solid #d9d9d9' }}>
											<Grid size={12}>
												<Grid container alignItems='center' justifyContent='space-between'>
													<Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
														<IconButton onClick={handleReset}>
															<ArrowBackIcon className='font22'/>
														</IconButton>
														<Typography className='font18 fontBold'>SIP</Typography>
													</Stack>
													<Stack direction='row' spacing={2} alignItems='center' justifyContent='center'>
														{isFavorite ? <FavoriteIcon className='font22' style={{ cursor: 'pointer', color:'#FF0000' }} onClick={handleFav}/> : <FavoriteBorderIcon className='font22 primaryCl' style={{ cursor: 'pointer' }} onClick={handleFav}/>}
														<InfoIcon className='font22 primaryCl'/>
														<ArchiveIcon className='font22 primaryCl'/>
													</Stack>
												</Grid>
											</Grid>
											<Grid size={12}>
												<Stack direction='row' justifyContent='space-between' alignItems='center'>
													{tabList.map((list, tabIndex) => (
														<Stack key={tabIndex} direction='row' spacing={0.5} alignItems='center' justifyContent='center' className={activeTab === list.value ? 'activeTab' : 'inActiveTab'} onClick={() => handleTabChange(list.value)}>
															{list.icon}
															<Typography className='fontBold font13'>{list.title}</Typography>
														</Stack>
													))}
												</Stack>
											</Grid>
										</Grid>
										<Box ref={ref} sx={{ height: '550px', overflow: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
											<TabContext value={activeTab}>
												<Box sx={{ position: 'relative', overflow: 'hidden', p: 2 }}>
													{tabList && tabList.map((list) => (
														<Slide key={list.value} direction={direction} in={activeTab === list.value} mountOnEnter unmountOnExit>
															<TabPanel value={list.value} sx={{ p: 0 }}>
																{activeTab === '1' &&
																	<Grid container spacing={3}>
																		{/* form */}
																		<Grid size={12} className='boxLayout' p={3} pb={2}>
																			<Grid container spacing={2}>
																				<Grid size={12}>
																					<InputLabel label='Sip Frequency' name='sipFrequency'/>
																					<Select
																						size='small'
																						fullWidth
																						className='appInput'
																						variant='outlined'
																						value={formData.sipFrequency}
																						name='sipFrequency'
																						IconComponent={KeyboardArrowDownIcon}
																						onChange={handleChangeInput}
																						MenuProps={{
																							disablePortal: true,
																							PaperProps: { sx: { width: 260, maxWidth: 260, overflowX: 'hidden' } },
																							anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
																							transformOrigin: { vertical: 'top', horizontal: 'left' }
																						}}
																						sx={{ 
																							borderRadius: '8px', 
																							paddingBottom: '4px', 
																							'& .MuiSelect-select': { padding: '16px 10px 6px 10px' },
																							'& .MuiSelect-icon': {
																								transition: 'transform 0.2s ease',
																								marginTop: '3px'
																							},
																							'& .MuiSelect-iconOpen': {
																								transform: 'rotate(180deg)'
																							}
																						}}
																					>
																						{dropInfo?.map((opt) => (
																							<MenuItem key={opt.value} value={opt.value} className='riderDropMenu'>
																								<span>{opt.label}</span>
																							</MenuItem>
																						))}
																					</Select>
																				</Grid>
																				<Grid size={12}>
																					<InputLabel label='Sip Amount' name='sipAmount'/>
																					<Box sx={{ position: 'relative' }}>
																						<TextField
																							variant='outlined'
																							size='small'
																							className='appInput'
																							value={formData.sipAmount}
																							name='sipAmount'
																							onChange={handleChangeInput}
																							inputRef={inputRef}
																							onFocus={() => handleFocus('focus')}
																							handleBlur={() => handleFocus('blur')}
																							inputProps={{
																								inputMode: 'numeric',
																								pattern: '[0-9]*',
																								maxLength: 10
																							}}
																						/>
																						<Box sx={{ position: 'absolute', bgcolor: '#fff', top: 'calc(50% - 8px)', left: '-9px', height: '20px', width: '20px', border: '1px solid grey', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'grey' }} className='font13'>₹</Box>
																					</Box>
																					<span className={`font12 fontBold ${formData.sipAmount ? 'primaryCl' : 'errorCl'}`}>{numberToWords(formData.sipAmount, 'Rupees') || 'Min Sip Amount is 0 !'}</span>
																				</Grid>
																				<Grid size={12}>
																					<InputLabel label='Investment Period' name='investmentPeriod'/>
																					<Grid container spacing={2}>
																						<Grid size={6}>
																							<TextField
																								variant='outlined'
																								size='small'
																								className='appInput'
																								value={formData.investmentYear}
																								name='investmentYear'
																								onChange={handleChangeInput}
																								inputProps={{
																									inputMode: 'numeric',
																									pattern: '[0-9]*',
																									maxLength: 2
																								}}
																							/>
																							<span className={`font12 fontBold  ${formData.investmentYear ? 'primaryCl' : 'errorCl'}`}>{numberToWords(formData.investmentYear, formData.investmentYear > 1 ? 'Years' : 'Year') || 'Min Invest Period is 0 !'}</span>
																						</Grid>
																						<Grid size={6}>
																							<Select
																								size='small'
																								fullWidth
																								className='appInput'
																								variant='outlined'
																								value={formData.investmentMonth}
																								name='investmentMonth'
																								onChange={handleChangeInput}
																								IconComponent={KeyboardArrowDownIcon}
																								sx={{ 
																									borderRadius: '8px', 
																									paddingBottom: '4px', 
																									'& .MuiSelect-select': { padding: '16px 10px 6px 10px' },
																									'& .MuiSelect-icon': {
																										transition: 'transform 0.2s ease',
																										marginTop: '3px'
																									},
																									'& .MuiSelect-iconOpen': {
																										transform: 'rotate(180deg)'
																									}
																								}}
																								MenuProps={{ disablePortal: true }}
																							>
																								{monthInfo?.map((opt) => (
																									<MenuItem key={opt.value} value={opt.value} className='riderDropMenu'>
																										<span>{opt.label}</span>
																									</MenuItem>
																								))}
																							</Select>
																							<span className='font12 primaryCl fontBold'>
																								{Number(formData.investmentMonth) > 0 ? numberToWords(formData.investmentMonth,formData.investmentMonth > 1 ? 'Months' : 'Month') : ''}
																							</span>
																						</Grid>
																					</Grid>
																				</Grid>
																				<Grid size={12}>
																					<InputLabel label='Expected Return' name='expectedReturn'/>
																					<Box sx={{ position: 'relative' }}>
																						<TextField
																							variant='outlined'
																							size='small'
																							className='appInput'
																							value={formData.expectedReturn}
																							name='expectedReturn'
																							onChange={handleChangeInput}
																							inputProps={{
																								inputMode: 'numeric',
																								pattern: '[0-9]*',
																								maxLength: 2
																							}}
																						/>
																						<Box sx={{ position: 'absolute', bgcolor: '#fff', top: 'calc(50% - 8px)', left: '-9px', height: '20px', width: '20px', border: '1px solid grey', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'grey' }} className='font13'>%</Box>
																					</Box>
																					<span className={`font12 fontBold  ${formData.expectedReturn ? 'primaryCl' : 'errorCl'}`}>{numberToWords(formData.expectedReturn, 'Percent') || 'Min Invest Percent is 0 !'}</span>
																				</Grid>
																				<Grid size={12} mt={2}>
																					<Stack direction='row' alignItems='center' justifyContent='space-between' spacing={3}>
																						<Button variant='contained' color='#fff' fullWidth className='fromButton' onClick={handleReset}>Reset</Button>
																						<Button variant='contained' color='primary' fullWidth className='fromButton' onClick={handleCalculate}>Calculate</Button>
																					</Stack>
																				</Grid>
																			</Grid>
																		</Grid>
																		{showRslt &&
																			<>
																				{/* sum */}	
																				<Grid size={12} className={`boxLayout uShapeBg ${showPopper && 'popperBg'}`}>
																					<Stack>
																						<Stack p={2} pb={0} direction='row' alignItems='center' justifyContent='space-between'>
																							<Typography>Total Invest</Typography>
																							<Typography>{formatINR(rsltData.totalInvestment)}</Typography>
																						</Stack>
																						<Stack p={2} py={1} direction='row' alignItems='center' justifyContent='space-between'>
																							<Typography>Returns</Typography>
																							<Typography>{formatINR(rsltData.gainLoss)}</Typography>
																						</Stack>
																						<Divider sx={{ borderStyle: 'dashed' }} />
																						<Stack p={2} py={1} direction='row' alignItems='center' justifyContent='space-between'>
																							<Typography color='#009E12' className='fontBold'>Value at maturity</Typography>
																							<Typography color='#009E12' className='fontBold'>{formatINR(rsltData.maturityValue)}</Typography>
																						</Stack>
																					</Stack>
																				</Grid>

																				{/* summary */}
																				<Grid size={12} className='boxLayout uShapeBg' p={2}>
																					<Typography className='font15 txtCenter'>
																						By investing&nbsp;<span className='fontBold'>{formatINR(rsltData?.sipAmount)}</span> {rsltData?.sipFrequency} for <span className='fontBold'>{rsltData?.investmentYear} years {rsltData?.investmentMonth && Number(rsltData?.investmentMonth) !== 0 ? `${rsltData?.investmentMonth} ${Number(rsltData?.investmentMonth) > 1 ? 'months' : 'month'}` : ''}</span> at <span className='fontBold'>{rsltData?.expectedReturn}%</span>. your total investment of&nbsp;<span className='fontBold'>{formatINR(rsltData?.totalInvestment)}</span> could earn returns of <span className='fontBold'>&nbsp;{formatINR(rsltData?.gainLoss)}</span>,
																						resulting in a maturity value of <span style={{ color: '#009E12' }} className='fontBold'>&nbsp;{formatINR(rsltData.maturityValue)}.</span>
																					</Typography>
																				</Grid>
																			</>
																		}
																	</Grid>
																}
																{activeTab === '2' && 
																	<Grid container spacing={3}>
																		{/* overview */}
																		<Grid size={12} className='boxLayout uShapeBg'>
																			<Stack>
																				<Stack p={2} py={1.5} pb={0} direction='row' alignItems='center' justifyContent='space-between'>
																					<Typography>SIP Amount</Typography>
																					<Typography>{formatINR(rsltData?.sipAmount)}</Typography>
																				</Stack>
																				<Stack p={2} py={1.5} pb={0} direction='row' alignItems='center' justifyContent='space-between'>
																					<Typography>Interest rate (%)</Typography>
																					<Typography>{rsltData?.expectedReturn}</Typography>
																				</Stack>
																				<Stack p={2} py={1.5} pb={0} direction='row' alignItems='center' justifyContent='space-between'>
																					<Typography>Investment Period</Typography>
																					<Typography>{rsltData?.investmentYear} years {rsltData?.investmentMonth && Number(rsltData?.investmentMonth) !== 0 ? `${rsltData?.investmentMonth} ${Number(rsltData?.investmentMonth) > 1 ? 'months' : 'month'}` : ''}</Typography>
																				</Stack>
																				<Stack p={2} py={1.5} pb={0} direction='row' alignItems='center' justifyContent='space-between'>
																					<Typography>SIP Frequency</Typography>
																					<Typography>{rsltData?.sipFrequency}</Typography>
																				</Stack>
																				<Stack p={2} py={1.5} pb={0} direction='row' alignItems='center' justifyContent='space-between'>
																					<Typography>Total Invest</Typography>
																					<Typography>{formatINR(rsltData.totalInvestment)}</Typography>
																				</Stack>
																				<Stack p={2} py={1.5} direction='row' alignItems='center' justifyContent='space-between'>
																					<Typography>Returns</Typography>
																					<Typography>{formatINR(rsltData.gainLoss)}</Typography>
																				</Stack>
																				<Divider sx={{ borderStyle: 'dashed' }} />
																				<Stack p={2} py={1.5} direction='row' alignItems='center' justifyContent='space-between'>
																					<Typography color='#009E12' className='fontBold'>Value at maturity</Typography>
																					<Typography color='#009E12' className='fontBold'>{formatINR(rsltData.maturityValue)}</Typography>
																				</Stack>
																			</Stack>
																		</Grid>

																		{/* summary */}
																		<Grid size={12} className='boxLayout uShapeBg' p={2}>
																			<Typography className='font15 txtCenter'>
																				By investing&nbsp;<span className='fontBold'>{formatINR(rsltData?.sipAmount)}</span> {rsltData?.sipFrequency} for <span className='fontBold'>{rsltData?.investmentYear} years {rsltData?.investmentMonth && Number(rsltData?.investmentMonth) !== 0 ? `${rsltData?.investmentMonth} ${Number(rsltData?.investmentMonth) > 1 ? 'months' : 'month'}` : ''}</span> at <span className='fontBold'>{rsltData?.expectedReturn}%</span>. your total investment of&nbsp;<span className='fontBold'>{formatINR(rsltData.totalInvestment)}</span> could earn returns of <span className='fontBold'>&nbsp;{formatINR(rsltData.gainLoss)}</span>,
																				resulting in a maturity value of <span style={{ color: '#009E12' }} className='fontBold'>&nbsp;{formatINR(rsltData.maturityValue)}.</span>
																			</Typography>
																		</Grid>

																		{/* table  */}
																		<Grid size={12}>
																			<Grid container>
																				<Grid size={12} className='reportTableHead'>
																					<Stack direction='row' spacing={1} divider={<Divider orientation='vertical' flexItem sx={{ height: '25px', transform: 'translateY(8px)' }}/>} alignItems='center'>
																						<Grid size={2} className='txtCenter font14'>Year</Grid>
																						<Grid size={4} className='txtCenter font14'>Investment Amount</Grid>
																						<Grid size={3} className='txtCenter font14'>Maturity Value</Grid>
																						<Grid size={3} className='txtCenter font14'>Capital Gain/Loss</Grid>
																					</Stack>
																				</Grid>
																				<Grid size={12} sx={{ border: '1px solid #d9d9d9', borderRadius: '0px 0px 10px 10px', py: '5px', pb: '10px' }}>
																					<Stack  divider={<Divider orientation='horizontal' flexItem sx={{ borderStyle: 'dashed' }}/>} >
																						{rsltData.tableData && rsltData.tableData.map((tblDatas, index) => {
																							return(
																								<>
																									<Stack direction='row' sx={{ bgcolor: index % 2 !== 0 ? '#fafafa' : '#fff' }} py={1} spacing={1} divider={<Divider orientation='vertical' flexItem sx={{ height: '20px', transform: 'translateY(4px)' }}/>} alignItems='center'>
																										<Grid size={2.5} className='txtCenter font14'>
																											<Stack direction='row' spacing={1} alignItems='center' justifyContent='center' onClick={() => handleSplitTable(tblDatas.year)} style={{ cursor: 'pointer' }}>
																												<AddCircleOutlineIcon className={`font14 ${tblDatas.year === selectedYear && 'primaryCl'}`}/> <Typography className='font13'>{tblDatas.year}</Typography>
																											</Stack>
																										</Grid>
																										<Grid size={3.5} className='txtCenter font13'>{amountFormatter(tblDatas?.investedAmount)}</Grid>
																										<Grid size={3} className='txtCenter font13'>{amountFormatter(tblDatas?.maturityValue)}</Grid>
																										<Grid size={3} className='txtCenter font13'>{amountFormatter(tblDatas?.gainLoss)}</Grid>
																									</Stack>
																									{tblDatas.year === selectedYear &&
																										tblDatas.periods.map((monthData, monthKey) => (
																											<Stack key={monthKey} direction='row' sx={{ bgcolor: index % 2 !== 0 ? '#fafafa' : '#fff' }} spacing={1} py={1} divider={<Divider orientation='vertical' flexItem sx={{ height: '20px', transform: 'translateY(4px)' }}/>} alignItems='center'>
																												<Grid size={2.5} className='txtCenter font14'>
																													<Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
																														<Box className='font13'>{monthData?.period}</Box>
																													</Stack>
																												</Grid>
																												<Grid size={3.5} className='txtCenter font13'>{amountFormatter(monthData?.investedAmount)}</Grid>
																												<Grid size={3} className='txtCenter font13'>{amountFormatter(monthData?.maturityValue)}</Grid>
																												<Grid size={3} className='txtCenter font13'>{amountFormatter(monthData?.gainLoss)}</Grid>
																											</Stack>
																										))
																									}
																								</>
																							);
																						})}
																					</Stack>
																				</Grid>
																			</Grid>
																		</Grid>
																	</Grid>
																}
																{activeTab === '3' && 
																	<Grid container rowSpacing={3} justifyContent='flex-end'>
																		<Grid size={{ md: 9, xs: 10 }}>
																			<Stack direction='row' alignItems='center' justifyContent='space-between' className='chartSwitchWrapper'>
																				<Box
																					className='chartActiveBg'
																					sx={{ transform: chartMode === 'pie' ? 'translateX(0%)' : 'translateX(100%)' }}
																				/>
																				<Box
																					className={`font14 chartSwitch ${chartMode === 'pie' && 'activeChart'}`}
																					onClick={() => handlechartMode('pie')}
																				>
																					Pie Chart
																				</Box>
																				<Box
																					className={`font14 chartSwitch ${chartMode === 'graph' && 'activeChart'}`}
																					onClick={() => handlechartMode('graph')}
																				>
																					Graph
																				</Box>
																			</Stack>
																		</Grid>

																		{chartMode === 'graph' && 
																			<Grid size={12}>
																				<Grid container spacing={1} rowSpacing={2}>
																					<Grid size={6.5}>
																						<Stack direction='row' spacing={1} px={1.5} py={1} alignItems='center' sx={{ borderRadius: '13px', bgcolor: '#e4f3e6' }}>
																							<Box sx={{ width: '13px', height: '13px', borderRadius: '50%', bgcolor: '#5fba63' }}/>
																							<Typography className='font13 fontBold'>Investment Amount</Typography>
																						</Stack>
																					</Grid>
																					<Grid size={5.5}>
																						<Stack direction='row' spacing={1} px={1.5} py={1} alignItems='center' sx={{ borderRadius: '13px', bgcolor: '#d5dffa' }}>
																							<Box sx={{ width: '13px', height: '13px', borderRadius: '50%', bgcolor: '#1d6ee2' }}/>
																							<Typography className='font13 fontBold'>Maturity Value</Typography>
																						</Stack>
																					</Grid>
																					<Grid size={6.5}>
																						<Stack direction='row' spacing={1} px={1.5} py={1} alignItems='center' sx={{ borderRadius: '13px', bgcolor: '#f3d4fb' }}>
																							<Box sx={{ width: '13px', height: '13px', borderRadius: '50%', bgcolor: '#b800e6' }}/>
																							<Typography className='font13 fontBold'>Capital Gain/Loss</Typography>
																						</Stack>
																					</Grid>
																				</Grid>
																			</Grid>
																		}
																		{/* chart  */}
																		<Grid size={12} className='boxLayout' sx={{ bgcolor: chartMode === 'graph' && '#f2f2f2' }} p={1}>
																			{chartMode === 'pie' ?
																				<SIPPieChart
																					totalInvestment={rsltData.totalInvestment}
																					gainLoss={rsltData.gainLoss}
																				/>
																				:
																				<SIPLineChart tableData={rsltData.tableData} frequency={rsltData?.sipFrequency}/>
																			}
																		</Grid>
																		{chartMode === 'pie' &&
																			<>
																				{/* summary  */}
																				<Grid size={12} className='boxLayout' p={3}>
																					<Stack direction='row' justifyContent='space-between' alignItems='center' mb={1}>
																						<Stack direction='row' spacing={1} alignItems='center'>
																							<Box sx={{ height: '10px', width: '10px', borderRadius: '50%', bgcolor: '#5B31A1' }}/>
																							<Typography>Total Invest</Typography>
																						</Stack>
																						<Typography>{formatINR(rsltData?.totalInvestment)}</Typography>
																					</Stack>
																					<Stack direction='row' justifyContent='space-between' alignItems='center'>
																						<Stack direction='row' spacing={1} alignItems='center'>
																							<Box sx={{ height: '10px', width: '10px', borderRadius: '50%', bgcolor: '#3dc269' }}/>
																							<Typography>Total Returns</Typography>
																						</Stack>
																						<Typography>{formatINR(rsltData?.gainLoss)}</Typography>
																					</Stack>
																				</Grid>
																				{/* overall */}
																				<Grid size={12} className='txtCenter'>
																					<Typography color='#009E12' className='fontBold font18'>Value at maturity</Typography>
																					<Typography color='#009E12' className='fontBold font18'>{formatINR(rsltData.maturityValue)}</Typography>
																				</Grid>
																			</>
																		}
																	</Grid>
																}
															</TabPanel>
														</Slide>
													))}
												</Box>
											</TabContext>
										</Box>
									</Box>
								</Box>
								{/* </div> */}
							</Box>
						</motion.div>
					</div>
					{/* </motion.div> */}
				</Grid>
			</Grid>
		</Container>
	);
};

export default Calculator;
